import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import React, { useState } from "react";
import { extractEmails } from "./helpers";

interface Props {
  emails: string[];
  /** Called with the addresses found, however they arrived. */
  onChange: (emails: string[]) => void;
  /** Cleared alongside the emails — the panels reset their match and result. */
  onReset?: () => void;
}

/**
 * Getting a list of member emails in, by pasting or by file.
 *
 * Pasting is the common case: a list usually comes out of another screen or a
 * message, and making a CSV of it first is a chore. The file upload stays for
 * lists that genuinely live in a spreadsheet.
 *
 * Both go through the same parser, which picks addresses out of whatever it's
 * given — commas, new lines, spaces, or a column pasted straight out of Sheets.
 */
const EmailsInput: React.FC<Props> = ({ emails, onChange, onReset }) => {
  const [pasted, setPasted] = useState("");
  const [fileName, setFileName] = useState("");

  const apply = (text: string, from: string) => {
    setFileName(from);
    onChange(extractEmails(text));
    onReset?.();
  };

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setPasted("");
    apply(await file.text(), file.name);
  };

  return (
    <Stack spacing={1}>
      <TextField
        label="Paste emails"
        multiline
        minRows={3}
        value={pasted}
        onChange={(event) => {
          setPasted(event.target.value);
          apply(event.target.value, "");
        }}
        placeholder="anna@example.com, bea@example.com, cara@example.com"
        helperText="Separated by commas, spaces or new lines — whatever you paste, the addresses are picked out."
        fullWidth
      />

      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Button variant="outlined" component="label" size="small">
          📄 Or upload a file (.csv)
          <input hidden type="file" accept=".csv,.txt" onChange={handleFile} />
        </Button>

        <Typography variant="caption" color="text.secondary">
          {emails.length > 0
            ? `${fileName ? `${fileName} — ` : ""}${emails.length} email${
                emails.length === 1 ? "" : "s"
              } found`
            : "The platform finds each member for you."}
        </Typography>
      </Box>
    </Stack>
  );
};

export default EmailsInput;
